import { Data, Layout } from 'plotly.js';
import { useState } from 'react';
import PlotlyComponent from 'react-plotly.js';
import { Dfs2SpectralItem, Dfs2SpectralMetadata } from '../../../../../../types/waveSpectra';
import {
  COMPASS_TICK_LABELS,
  COMPASS_TICK_VALUES,
  computeBinEdges,
  computeRadialTicks,
  formatCursorTime,
  formatPeriodForTooltip,
  getPolarChartTitle,
  LOG_RADIAL_FLOOR,
  spectrum2dUnitLabel,
  SPECTRUM_COLOR_SCALE,
  SpectrumAxisMode,
  UI_REVISION_TOKEN,
} from './helpers';
import { RESET_AXES_ICON, spectraChartConfig } from './spectra-charts.config';

const Plot = PlotlyComponent as any;

interface WaveSpectraEnergyDensityPolarProps {
  metadata: Dfs2SpectralMetadata;
  item: Dfs2SpectralItem;
  time: string;
  axisMode: SpectrumAxisMode;
  colorScale?: Array<[number, string]>;
  isLightPlotTheme?: boolean;
}

export const WaveSpectraEnergyDensityPolar = ({
  metadata,
  item,
  time,
  axisMode,
  colorScale = SPECTRUM_COLOR_SCALE,
  isLightPlotTheme = true,
}: WaveSpectraEnergyDensityPolarProps) => {
  const [radialAutoRange, setRadialAutoRange] = useState(true);
  const [radialViewKey, setRadialViewKey] = useState(0);

  const { freqs, dirs, periods, times } = metadata;

  const timeIdx = times.indexOf(time);
  const spectrum = item.spectrum_2d?.[timeIdx]; // [n_dirs][n_freqs]

  if (!spectrum || freqs.length < 2 || dirs.length < 1) {
    return null;
  }

  const radialValues = axisMode === 'period' ? periods : freqs;
  const rawEdges = computeBinEdges(radialValues);

  const edges =
    axisMode === 'period' ? rawEdges.map((e) => Math.max(e, LOG_RADIAL_FLOOR)) : rawEdges;

  const dTheta = dirs.length > 1 ? dirs[1] - dirs[0] : 360;
  const { tickStep, tMax } = computeRadialTicks(Math.max(...edges));

  const theta: number[] = [];
  const base: number[] = [];
  const r: number[] = [];
  const color: number[] = [];
  const customdata: Array<[number, number, number, string]> = [];

  for (let j = 0; j < dirs.length; j++) {
    for (let i = 0; i < freqs.length; i++) {
      theta.push(dirs[j]);
      const outerEdge = Math.max(edges[i], edges[i + 1]);
      const innerEdge = Math.min(edges[i], edges[i + 1]);
      base.push(innerEdge);
      r.push(outerEdge - innerEdge);
      color.push(spectrum[j][i]);
      customdata.push([dirs[j], freqs[i], spectrum[j][i], formatPeriodForTooltip(periods[i])]);
    }
  }

  const hoverTemplatePolarPlot = `θ: %{customdata[0]:.1f}°<br>T: %{customdata[3]} s<br>f: %{customdata[1]:.4f} Hz<br>E: %{customdata[2]:.3e} ${spectrum2dUnitLabel(
    item.unit
  )}<extra></extra>`;

  const traces: Partial<Data>[] = [
    {
      type: 'barpolar',
      theta,
      r,
      base,
      width: dTheta,
      customdata,
      marker: {
        color,
        colorscale: colorScale,
        cmin: 0,
        cmax: Math.max(...color),
        colorbar: {
          thickness: 14,
          title: { text: `Energy density [${spectrum2dUnitLabel(item.unit)}]`, side: 'right' },
          tickfont: { size: 10 },
          exponentformat: 'e',
        },
        line: { width: 0 },
      },
      showlegend: false,
      hovertemplate: hoverTemplatePolarPlot,
    } as unknown as Partial<Data>,
  ];

  const layout: Partial<Layout> = {
    autosize: true,
    margin: { l: 40, r: 110, t: 50, b: 40 },
    title: {
      text: `${getPolarChartTitle(axisMode)} — ${formatCursorTime(time)}`,
      font: { size: 13 },
    },
    polar: {
      bgcolor: 'white',
      radialaxis: {
        type: axisMode === 'period' ? 'linear' : 'log',
        ...(radialAutoRange
          ? { autorange: true }
          : axisMode === 'period'
          ? { range: [Math.log10(LOG_RADIAL_FLOOR), Math.log10(tMax)] }
          : { range: [0, tMax], dtick: tickStep }),
        ticksuffix: axisMode === 'period' ? ' s' : ' Hz',
        tickfont: { size: 10, color: isLightPlotTheme ? '#1e293b' : '#e2e8f0' },
        gridcolor: 'rgba(226, 232, 240, 0.35)',
        uirevision: `${UI_REVISION_TOKEN}-${axisMode}-${radialViewKey}`,
      },
      angularaxis: {
        rotation: 90,
        direction: 'clockwise',
        tickvals: COMPASS_TICK_VALUES,
        ticktext: COMPASS_TICK_LABELS,
        tickfont: { size: 11 },
        uirevision: `${UI_REVISION_TOKEN}-${axisMode}`,
      },
    },
    plot_bgcolor: 'white',
    paper_bgcolor: 'white',
  };

  const config = {
    ...spectraChartConfig,
    modeBarButtonsToAdd: [
      {
        name: 'resetPolarAxis',
        title: 'Reset axes',
        icon: RESET_AXES_ICON,
        click: () => {
          setRadialAutoRange(true);
          setRadialViewKey((key) => key + 1);
        },
      },
    ],
  };

  return (
    <Plot
      data={traces}
      layout={layout}
      useResizeHandler
      style={{ width: '100%', height: '100%' }}
      config={config}
    />
  );
};
